<template>
  <div class="fixed bottom-4 right-6 z-50 select-none">
    <div class="w-[360px] h-[560px] rounded-2xl shadow-xl border border-gray-200 bg-white overflow-hidden flex flex-col">

      <!-- Header -->
      <div class="px-4 py-3 flex items-center justify-between border-b border-gray-100 shrink-0 h-[60px]">
        <div class="flex items-center gap-3">
          <div v-if="ui.localPresenceAvailable" class="flex items-center gap-3">
            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                class="sr-only peer"
                :checked="ui.localPresenceEnabled"
                @change="setLocalPresenceEnabled($event.target.checked)"
              />
              <div class="w-10 h-5 rounded-full transition relative bg-gray-300 peer-checked:bg-tvOrange">
                <span
                  class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition"
                  :class="ui.localPresenceEnabled ? 'translate-x-5' : ''"
                ></span>
              </div>
            </label>
            <div class="leading-tight">
              <div class="text-sm font-semibold text-gray-900">Local Presence</div>
              <div class="text-xs font-medium text-gray-500">
                {{ ui.localPresenceEnabled ? 'Enabled' : 'Disabled' }}
              </div>
            </div>
          </div>

          <div v-else class="leading-tight">
            <div class="text-sm font-semibold text-gray-900">{{ displayName }}</div>
            <div class="flex items-center gap-2">
              <span
                class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                :class="statusPillClass"
              >
                <span class="h-1.5 w-1.5 rounded-full" :class="statusDotClass"></span>
                {{ statusLabel }}
              </span>
              <span v-if="extension && extension !== '—'" class="text-xs font-medium text-gray-500">
                • Ext {{ extension }}
              </span>
              <span v-if="isActiveCall" class="text-xs font-medium text-gray-500">
                • {{ prettyDuration }}
              </span>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <button
            class="h-9 w-9 rounded-xl hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center"
            @click="openDevices"
            title="Audio devices"
          >
            <Cog6ToothIcon class="h-5 w-5 text-gray-600" />
          </button>
          <button
            class="h-9 w-9 rounded-xl hover:bg-gray-50 active:bg-gray-100 flex items-center justify-center"
            @click="closePhone"
            title="Close"
          >
            <XMarkIcon class="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-4">
        <div
          v-if="lastError"
          class="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {{ lastError }}
        </div>

        <transition name="screen" mode="out-in">

          <!-- ==================== WLB ==================== -->
          <div v-if="isWlbActive" :key="'wlb'" class="min-h-[330px]">
            <div class="rounded-2xl border border-gray-200 bg-white p-4">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="text-xs font-semibold text-gray-500 mb-1">WLB Session</div>
                  <div class="text-lg font-bold text-gray-900 truncate">
                    {{ wlbTargetLabel || 'Target' }}
                  </div>
                  <div class="text-sm text-gray-600 truncate">
                    {{ wlbModeLabel }}
                    <span v-if="isActiveCall" class="text-gray-500"> • {{ prettyDuration }}</span>
                  </div>
                </div>
                <div
                  class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border"
                  :class="wlbModePillClass"
                >
                  {{ wlbModePillText }}
                </div>
              </div>

              <div class="grid grid-cols-3 gap-2 mt-4">
                <button
                  class="h-11 rounded-xl border font-semibold text-sm flex items-center justify-center gap-2"
                  :class="wlbButtonClass('listen')"
                  :disabled="wlbSwitchDisabled('listen')"
                  @click="switchWlb('listen')"
                  title="Listen"
                >
                  <SpeakerWaveIcon class="h-5 w-5" />
                  Listen
                </button>
                <button
                  class="h-11 rounded-xl border font-semibold text-sm flex items-center justify-center gap-2"
                  :class="wlbButtonClass('whisper')"
                  :disabled="wlbSwitchDisabled('whisper')"
                  @click="switchWlb('whisper')"
                  title="Whisper"
                >
                  <MicrophoneIcon class="h-5 w-5" />
                  Whisper
                </button>
                <button
                  class="h-11 rounded-xl border font-semibold text-sm flex items-center justify-center gap-2"
                  :class="wlbButtonClass('barge')"
                  :disabled="wlbSwitchDisabled('barge')"
                  @click="switchWlb('barge')"
                  title="Barge"
                >
                  <MegaphoneIcon class="h-5 w-5" />
                  Barge
                </button>
              </div>

              <button
                class="mt-4 w-full h-12 rounded-xl bg-red-600 text-white font-bold hover:opacity-95 active:opacity-90"
                @click="hangupWlb"
              >
                End WLB
              </button>

              <div v-if="!isActiveCall" class="mt-3 text-xs text-gray-500">
                Connecting WLB session…
              </div>
            </div>
          </div>

          <!-- ==================== INCOMING RINGING ==================== -->
          <div v-else-if="isIncomingRinging" :key="'incoming'" class="min-h-[330px]">
            <div class="rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-green-50/40 p-5">
              <div class="text-center">
                <div class="w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center bg-green-100 ring-4 ring-green-100/50 animate-pulse">
                  <template v-if="incomingContactMatch">
                    <span class="text-xl font-bold text-green-700">{{ contactInitials(incomingContactMatch.name) }}</span>
                  </template>
                  <PhoneIcon v-else class="h-7 w-7 text-green-600" />
                </div>

                <div class="text-xs font-semibold text-green-600 uppercase tracking-wide mb-1">Incoming Call</div>
                <div class="text-xl font-bold text-gray-900">
                  {{ incomingContactMatch ? incomingContactMatch.name : (callerName || 'Unknown Caller') }}
                </div>
                <div v-if="callerNumber" class="text-sm text-gray-500 mt-0.5">{{ callerNumber }}</div>
                <div v-if="incomingContactMatch && incomingContactMatch.company" class="text-xs text-gray-400 mt-0.5">
                  {{ incomingContactMatch.company }}
                </div>
              </div>

              <div class="flex gap-3 mt-6">
                <button
                  class="flex-1 h-12 rounded-xl bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 active:bg-gray-200 transition"
                  @click="hangup"
                >
                  Decline
                </button>
                <button
                  class="flex-1 h-12 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 active:bg-green-600 transition shadow-sm"
                  @click="answer"
                >
                  Answer
                </button>
              </div>
            </div>
          </div>

          <!-- ==================== ADD CONTACT FORM ==================== -->
          <div v-else-if="addContactMode" :key="'addcontact'">
            <div class="flex items-center gap-2 mb-4">
              <button
                class="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                @click="cancelAddContact"
              >
                <ArrowLeftIcon class="h-4 w-4 text-gray-600" />
              </button>
              <div class="text-sm font-semibold text-gray-900">New Contact</div>
            </div>

            <div class="space-y-3">
              <div>
                <label class="text-xs font-semibold text-gray-500 mb-1 block">Name *</label>
                <input
                  v-model="addContactForm.name"
                  class="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-500 mb-1 block">Phone</label>
                <input
                  v-model="addContactForm.phone"
                  class="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="Phone number"
                  inputmode="tel"
                />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-500 mb-1 block">Company</label>
                <input
                  v-model="addContactForm.company"
                  class="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="Company name"
                />
              </div>
              <div>
                <label class="text-xs font-semibold text-gray-500 mb-1 block">Email</label>
                <input
                  v-model="addContactForm.email"
                  class="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="Email address"
                  type="email"
                />
              </div>
            </div>

            <button
              class="mt-4 w-full h-11 rounded-xl font-semibold text-white transition"
              :class="addContactForm.name.trim() ? 'bg-tvOrange hover:opacity-95' : 'bg-gray-300 cursor-not-allowed'"
              :disabled="!addContactForm.name.trim() || addContactSaving"
              @click="saveNewContact"
            >
              {{ addContactSaving ? 'Saving...' : 'Save Contact' }}
            </button>
          </div>

          <!-- ==================== ACTIVE CALL ==================== -->
          <div v-else-if="inCall" :key="'call'" class="min-h-[330px]">
            <div class="rounded-2xl border border-gray-200 bg-white p-4">

              <!-- Caller info (centered) -->
              <div class="text-center mb-4">
                <div
                  class="w-14 h-14 rounded-full mx-auto mb-3 flex items-center justify-center transition-colors"
                  :class="isActiveCall ? (onHold ? 'bg-amber-100' : 'bg-green-100') : 'bg-blue-100'"
                >
                  <template v-if="activeCallContact">
                    <span class="text-lg font-bold" :class="isActiveCall ? (onHold ? 'text-amber-700' : 'text-green-700') : 'text-blue-700'">
                      {{ contactInitials(activeCallContact.name) }}
                    </span>
                  </template>
                  <PhoneIcon v-else class="h-6 w-6" :class="isActiveCall ? (onHold ? 'text-amber-600' : 'text-green-600') : 'text-blue-600'" />
                </div>

                <div class="text-lg font-bold text-gray-900">{{ callTitle }}</div>
                <div v-if="callSubtitle" class="text-sm text-gray-500">{{ callSubtitle }}</div>

                <div class="mt-1.5 flex items-center justify-center gap-1.5">
                  <span
                    v-if="isActiveCall"
                    class="h-2 w-2 rounded-full animate-pulse"
                    :class="onHold ? 'bg-amber-500' : 'bg-green-500'"
                  ></span>
                  <span
                    class="text-sm font-medium"
                    :class="isActiveCall ? (onHold ? 'text-amber-600' : 'text-green-600') : 'text-blue-500'"
                  >
                    {{ callStateLabel }}
                    <template v-if="isActiveCall"> {{ prettyDuration }}</template>
                  </span>
                </div>
              </div>

              <!-- Action buttons with labels -->
              <div class="grid grid-cols-3 gap-2">
                <button
                  class="h-14 rounded-xl border flex flex-col items-center justify-center gap-0.5 transition"
                  :class="[
                    muted ? 'bg-red-50 border-red-200 text-red-600' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700',
                    !isActiveCall ? 'opacity-50 cursor-not-allowed' : ''
                  ]"
                  :disabled="!isActiveCall"
                  @click="toggleMute"
                >
                  <MicrophoneIcon v-if="!muted" class="h-5 w-5" />
                  <NoSymbolIcon v-else class="h-5 w-5" />
                  <span class="text-[10px] font-semibold leading-tight">{{ muted ? 'Unmute' : 'Mute' }}</span>
                </button>

                <button
                  class="h-14 rounded-xl border flex flex-col items-center justify-center gap-0.5 transition"
                  :class="onHold ? 'bg-amber-50 border-amber-200 text-amber-600' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'"
                  :disabled="!isActiveCall"
                  @click="toggleHold"
                >
                  <PauseCircleIcon v-if="!onHold" class="h-5 w-5" />
                  <PlayCircleIcon v-else class="h-5 w-5" />
                  <span class="text-[10px] font-semibold leading-tight">{{ onHold ? 'Resume' : 'Hold' }}</span>
                </button>

                <button
                  class="h-14 rounded-xl border flex flex-col items-center justify-center gap-0.5 transition"
                  :class="keypadOpen ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-200 bg-white hover:bg-gray-50 text-gray-700'"
                  :disabled="!isActiveCall || transferMode"
                  @click="toggleKeypad"
                >
                  <HashtagIcon class="h-5 w-5" />
                  <span class="text-[10px] font-semibold leading-tight">Keypad</span>
                </button>
              </div>

              <!-- Transfer / Keypad panels -->
              <div class="mt-3 overflow-hidden">
                <transition name="slide" mode="out-in">
                  <div v-if="transferMode" :key="'transfer'">
                    <div class="flex items-center justify-between">
                      <div class="text-xs font-semibold text-gray-500">Transfer to</div>
                      <button class="text-xs font-semibold text-gray-700 hover:text-gray-900" @click="toggleTransferMode">
                        Cancel
                      </button>
                    </div>

                    <div class="relative mt-2">
                      <input
                        v-model="transferTarget"
                        inputmode="tel"
                        class="w-full h-12 rounded-xl border border-gray-200 px-3 pr-11 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                        placeholder="e.g. 203 or 10-digit number"
                        @keydown.enter.prevent="doTransfer"
                      />
                      <button
                        v-if="transferTarget"
                        class="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700"
                        @click="transferBackspace"
                        title="Backspace"
                      >
                        <span class="text-sm font-bold">&#9003;</span>
                      </button>
                    </div>

                    <div class="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>{{ transferHint }}</span>
                    </div>

                    <div class="mt-3 grid grid-cols-3 gap-2">
                      <button
                        v-for="k in keypadKeys"
                        :key="'t' + k"
                        class="h-12 rounded-xl bg-gray-100 hover:bg-gray-200 font-bold text-gray-900"
                        @click="transferAppend(k)"
                      >
                        {{ k }}
                      </button>
                    </div>

                    <button
                      class="mt-3 w-full h-12 rounded-xl font-bold text-white transition"
                      :class="transferBtnClass"
                      :disabled="!transferEnabled"
                      @click="doTransfer"
                    >
                      Transfer
                    </button>
                  </div>

                  <div v-else-if="keypadOpen" :key="'keypad'">
                    <div class="grid grid-cols-3 gap-2">
                      <button
                        v-for="k in keypadKeys"
                        :key="k"
                        class="h-12 rounded-xl bg-gray-100 hover:bg-gray-200 font-bold text-gray-900"
                        @click="sendDTMF(k)"
                      >
                        {{ k }}
                      </button>
                    </div>
                  </div>

                  <div v-else :key="'none'"></div>
                </transition>
              </div>

              <!-- Save Contact + Transfer row -->
              <div class="mt-3 grid gap-2" :class="activeCallNumber && !activeCallContact ? 'grid-cols-2' : 'grid-cols-1'">
                <button
                  v-if="activeCallNumber && !activeCallContact"
                  class="h-11 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center gap-2 text-sm font-semibold text-gray-700 transition"
                  @click="showAddContact(activeCallNumber, callTitle)"
                >
                  <UserPlusIcon class="h-4 w-4" />
                  Save Contact
                </button>
                <button
                  class="h-11 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center gap-2 text-sm font-semibold text-gray-700 transition"
                  :class="!canTransfer ? 'opacity-40 cursor-not-allowed' : ''"
                  :disabled="!canTransfer"
                  @click="toggleTransferMode"
                >
                  <ArrowUturnRightIcon class="h-4 w-4" />
                  Transfer
                </button>
              </div>

              <!-- End Call -->
              <button
                class="mt-2 w-full h-12 rounded-xl bg-gradient-to-r from-red-600 to-red-500 text-white font-bold hover:opacity-95 active:opacity-90 shadow-sm"
                @click="hangup"
              >
                End Call
              </button>
            </div>
          </div>

          <!-- ==================== HOME TABS ==================== -->
          <div v-else :key="'home'">

            <!-- Tab bar -->
            <div class="flex bg-gray-100 rounded-xl p-1 mb-4">
              <button
                class="flex-1 py-2 text-sm font-semibold rounded-lg transition"
                :class="tab === 'dialer' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'"
                @click="setTab('dialer')"
              >
                Dialer
              </button>
              <button
                class="flex-1 py-2 text-sm font-semibold rounded-lg transition relative"
                :class="tab === 'recents' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'"
                @click="setTab('recents')"
              >
                Recents
              </button>
              <button
                class="flex-1 py-2 text-sm font-semibold rounded-lg transition"
                :class="tab === 'contacts' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600 hover:text-gray-900'"
                @click="setTab('contacts')"
              >
                Contacts
              </button>
            </div>

            <!-- ===== DIALER TAB ===== -->
            <div v-if="tab === 'dialer'">
              <div class="mb-3">
                <div class="text-xs font-semibold text-gray-500 mb-1">Dial a number or extension</div>
                <div class="relative">
                  <input
                    v-model="number"
                    inputmode="tel"
                    class="w-full h-12 rounded-xl border border-gray-200 px-3 pr-11 text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                    placeholder="e.g. 203 or 10-digit number"
                    @keydown.enter.prevent="dialNow"
                  />
                  <button
                    v-if="number"
                    class="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700"
                    @click="backspace"
                    title="Backspace"
                  >
                    <span class="text-sm font-bold">&#9003;</span>
                  </button>
                </div>
                <div class="mt-2 flex items-center justify-between text-xs text-gray-500">
                  <span>{{ dialHint }}</span>
                </div>
              </div>

              <div class="grid grid-cols-3 gap-2">
                <button
                  v-for="k in keypadKeys"
                  :key="k"
                  class="h-12 rounded-xl bg-gray-100 hover:bg-gray-200 font-bold text-gray-900"
                  @click="append(k)"
                >
                  {{ k }}
                </button>
              </div>

              <button
                class="mt-4 w-full h-12 rounded-xl font-bold transition"
                :class="callBtnClass"
                :disabled="!callButtonEnabledLocal"
                @click="dialNow"
              >
                Call
              </button>

              <div v-if="!canDial" class="mt-3 text-xs text-gray-500">
                Call disabled until you're <span class="font-semibold">Connected + Registered</span>.
              </div>
            </div>

            <!-- ===== RECENTS TAB (CDR) ===== -->
            <div v-else-if="tab === 'recents'">

              <!-- Loading skeletons -->
              <div v-if="cdrLoading" class="space-y-2">
                <div v-for="i in 5" :key="i" class="h-14 rounded-xl bg-gray-100 animate-pulse"></div>
              </div>

              <!-- Empty state -->
              <div v-else-if="!cdrDisplayRecents.length" class="text-center py-10">
                <div class="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-3 flex items-center justify-center">
                  <PhoneIcon class="h-5 w-5 text-gray-400" />
                </div>
                <div class="text-sm font-medium text-gray-500">No recent calls</div>
                <div class="text-xs text-gray-400 mt-1">Your call history will appear here</div>
              </div>

              <!-- Recents list -->
              <div v-else class="space-y-1">
                <div
                  v-for="r in cdrDisplayRecents"
                  :key="r.id"
                  class="flex items-center gap-2.5 rounded-xl px-3 py-2.5 hover:bg-gray-50 transition"
                >
                  <!-- Direction indicator -->
                  <div
                    class="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm font-bold"
                    :class="r.answered
                      ? (r.direction === 'outbound' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600')
                      : 'bg-red-50 text-red-500'"
                  >
                    {{ r.direction === 'outbound' ? '↗' : '↙' }}
                  </div>

                  <!-- Call info (click to redial) -->
                  <button class="flex-1 min-w-0 text-left" @click="dialRecent(r.number)">
                    <div class="text-sm font-medium text-gray-900 truncate">{{ r.label }}</div>
                    <div class="flex items-center gap-1.5 text-xs text-gray-500">
                      <span>{{ r.timeAgo }}</span>
                      <span v-if="r.duration" class="text-gray-400">{{ formatDuration(r.duration * 1000) }}</span>
                      <span v-if="!r.answered" class="text-red-500 font-medium">Missed</span>
                    </div>
                  </button>

                  <!-- Add contact -->
                  <button
                    v-if="r.showAddContact"
                    class="h-7 w-7 rounded-lg hover:bg-gray-100 flex items-center justify-center shrink-0"
                    @click="showAddContact(r.number, r.label)"
                    title="Save as contact"
                  >
                    <UserPlusIcon class="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                </div>
              </div>

              <!-- Refresh link -->
              <button
                v-if="!cdrLoading"
                class="mt-3 w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1"
                @click="loadCdrRecents"
              >
                {{ cdrDisplayRecents.length ? 'Refresh' : 'Retry' }}
              </button>
            </div>

            <!-- ===== CONTACTS TAB ===== -->
            <div v-else>

              <!-- Search -->
              <div class="relative mb-3">
                <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  v-model="contactsSearch"
                  class="w-full h-10 rounded-xl border border-gray-200 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="Search contacts..."
                />
              </div>

              <!-- Loading -->
              <div v-if="contactsLoading" class="space-y-2">
                <div v-for="i in 4" :key="i" class="h-14 rounded-xl bg-gray-100 animate-pulse"></div>
              </div>

              <!-- Empty -->
              <div v-else-if="!filteredContacts.length" class="text-center py-10">
                <div class="w-12 h-12 rounded-full bg-gray-100 mx-auto mb-3 flex items-center justify-center">
                  <UserCircleIcon class="h-6 w-6 text-gray-400" />
                </div>
                <div class="text-sm font-medium text-gray-500">
                  {{ contactsSearch ? 'No matches' : 'No contacts yet' }}
                </div>
                <div class="text-xs text-gray-400 mt-1">
                  {{ contactsSearch ? 'Try a different search' : 'Add contacts from the Contacts page' }}
                </div>
              </div>

              <!-- Contact list -->
              <div v-else class="space-y-0.5">
                <button
                  v-for="c in filteredContacts"
                  :key="c.id"
                  class="w-full text-left flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-gray-50 transition group"
                  @click="dialContact(c)"
                  :disabled="!c.phone"
                  :class="!c.phone ? 'opacity-60 cursor-default' : ''"
                >
                  <div class="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0 text-sm font-semibold text-gray-600">
                    {{ contactInitials(c.name) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-900 truncate">{{ c.name }}</div>
                    <div class="text-xs text-gray-500 truncate">
                      {{ c.phone ? formatPrettyPeer(c.phone) : (c.company || c.email || 'No phone') }}
                    </div>
                  </div>
                  <PhoneIcon v-if="c.phone" class="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition shrink-0" />
                </button>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- ==================== DEVICE SETTINGS MODAL ==================== -->
    <div v-if="showDevices" class="fixed inset-0 z-50">
      <div class="absolute inset-0 bg-black/30" @click="closeDevices"></div>
      <div class="absolute bottom-6 right-6 w-[360px] rounded-2xl bg-white border border-gray-200 shadow-2xl overflow-hidden">
        <div class="px-4 py-3 flex items-center justify-between border-b border-gray-100">
          <div class="font-semibold text-gray-900">Audio Devices</div>
          <button class="h-9 w-9 rounded-xl hover:bg-gray-50 flex items-center justify-center" @click="closeDevices">
            <XMarkIcon class="h-5 w-5 text-gray-600" />
          </button>
        </div>
        <div class="p-4 space-y-4">
          <div>
            <div class="text-xs font-semibold text-gray-500 mb-2">Microphone</div>
            <select
              class="w-full h-11 rounded-xl border border-gray-200 px-3 text-gray-900"
              :value="selectedInputId"
              @change="setInput($event.target.value)"
            >
              <option :value="null">Default</option>
              <option v-for="d in inputs" :key="d.deviceId" :value="d.deviceId">
                {{ d.label || `Mic ${d.deviceId.slice(0, 6)}` }}
              </option>
            </select>
          </div>
          <div>
            <div class="text-xs font-semibold text-gray-500 mb-2">Speaker</div>
            <select
              class="w-full h-11 rounded-xl border border-gray-200 px-3 text-gray-900"
              :value="selectedOutputId"
              @change="setOutput($event.target.value)"
            >
              <option :value="null">Default</option>
              <option v-for="d in outputs" :key="d.deviceId" :value="d.deviceId">
                {{ d.label || `Speaker ${d.deviceId.slice(0, 6)}` }}
              </option>
            </select>
            <div class="mt-2 text-xs text-gray-500">
              Note: speaker switching requires a browser that supports <span class="font-semibold">setSinkId()</span>.
            </div>
          </div>
          <button
            class="w-full h-11 rounded-xl bg-gray-100 hover:bg-gray-200 font-semibold text-gray-900"
            @click="refreshDevices"
          >
            Refresh device list
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import axios from 'axios'
import toneService from '@/services/phone/ToneService'
import {
  UserCircleIcon,
  Cog6ToothIcon,
  HashtagIcon,
  XMarkIcon,
  MicrophoneIcon,
  NoSymbolIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  ArrowUturnRightIcon,
  SpeakerWaveIcon,
  MegaphoneIcon,
  PhoneIcon,
  UserPlusIcon,
  MagnifyingGlassIcon,
  ArrowLeftIcon
} from '@heroicons/vue/24/outline'

export default {
  name: 'PhoneComponent',
  components: {
    UserCircleIcon,
    Cog6ToothIcon,
    HashtagIcon,
    XMarkIcon,
    MicrophoneIcon,
    NoSymbolIcon,
    PauseCircleIcon,
    PlayCircleIcon,
    ArrowUturnRightIcon,
    SpeakerWaveIcon,
    MegaphoneIcon,
    PhoneIcon,
    UserPlusIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon
  },

  data() {
    return {
      number: '',
      keypadOpen: false,
      keypadKeys: ['1','2','3','4','5','6','7','8','9','*','0','#'],
      transferMode: false,
      transferTarget: '',

      contactsList: [],
      contactsSearch: '',
      contactsLoading: false,

      cdrRecents: [],
      cdrLoading: false,

      addContactMode: false,
      addContactForm: { name: '', phone: '', company: '', email: '' },
      addContactSaving: false
    }
  },

  computed: {
    ...mapState('phone', ['ui', 'ua', 'call', 'devices', 'recents']),
    ...mapGetters('phone', [
      'statusLabel',
      'statusTone',
      'canDial',
      'inCall',
      'isActiveCall',
      'isIncomingRinging',
      'callButtonEnabled',
      'canTransfer',
      'isWlb',
      'wlbMode',
      'wlbTargetLabel'
    ]),

    displayName() {
      return this.ui?.displayName || process.env.VUE_APP_SIP_DISPLAY_NAME || 'User'
    },
    extension() {
      return this.ui?.extension || '—'
    },
    tab() {
      return this.ui.tab
    },
    showDevices() {
      return this.ui.showDevices
    },
    lastError() {
      return this.ua.lastError
    },
    muted() {
      return this.call.muted
    },
    onHold() {
      return this.call.onHold
    },

    callerName() {
      return this.call.fromName
    },
    callerNumber() {
      return this.formatPrettyPeer(this.call.from)
    },

    incomingContactMatch() {
      return this.matchContactByPhone(this.call.from)
    },

    activeCallContact() {
      const num = this.call.direction === 'outgoing' ? this.call.to : this.call.from
      return this.matchContactByPhone(num)
    },

    activeCallNumber() {
      if (this.call.direction === 'outgoing') return this.call.to
      return this.call.from
    },

    callStateLabel() {
      if (this.call.state === 'dialing') return 'Calling...'
      if (this.call.state === 'connecting') return 'Connecting...'
      if (this.call.state === 'active') return this.onHold ? 'On Hold' : 'On Call'
      if (this.call.state === 'ringing') return 'Ringing...'
      return 'Call'
    },

    callTitle() {
      if (this.call.direction === 'outgoing') {
        const match = this.matchContactByPhone(this.call.to)
        return match ? match.name : this.formatPrettyPeer(this.call.to)
      }
      if (this.call.direction === 'incoming') {
        const match = this.matchContactByPhone(this.call.from)
        if (match) return match.name
        return this.callerName || 'Unknown Caller'
      }
      return ''
    },

    callSubtitle() {
      if (this.call.direction === 'outgoing') {
        const match = this.matchContactByPhone(this.call.to)
        if (match) return this.formatPrettyPeer(this.call.to)
        return 'Outbound call'
      }
      if (this.call.direction === 'incoming') {
        const match = this.matchContactByPhone(this.call.from)
        if (match) return this.formatPrettyPeer(this.call.from)
        return this.callerNumber || ''
      }
      return ''
    },

    prettyDuration() {
      return this.formatDuration(this.call.durationMs || 0)
    },

    dialHint() {
      if (!this.number) return 'Use keypad or type.'
      return this.isValidTarget(this.number) ? 'Ready.' : 'Enter an extension (2-6 digits) or a full number.'
    },

    callButtonEnabledLocal() {
      return this.callButtonEnabled && this.isValidTarget(this.number)
    },

    callBtnClass() {
      if (!this.callButtonEnabledLocal) return 'bg-gray-200 text-gray-500 cursor-not-allowed'
      return 'bg-tvOrange text-white hover:opacity-95 active:opacity-90'
    },

    statusPillClass() {
      if (this.statusTone === 'good') return 'bg-green-50 text-green-700'
      if (this.statusTone === 'warn') return 'bg-amber-50 text-amber-700'
      return 'bg-gray-100 text-gray-700'
    },
    statusPillClassDark() {
      if (this.statusTone === 'good') return 'bg-green-500/20 text-green-400'
      if (this.statusTone === 'warn') return 'bg-amber-500/20 text-amber-400'
      return 'bg-white/10 text-gray-400'
    },
    statusDotClass() {
      if (this.statusTone === 'good') return 'bg-green-500'
      if (this.statusTone === 'warn') return 'bg-amber-500'
      return 'bg-gray-500'
    },

    inputs() {
      return this.devices.inputs || []
    },
    outputs() {
      return this.devices.outputs || []
    },
    selectedInputId() {
      return this.devices.selectedInputId
    },
    selectedOutputId() {
      return this.devices.selectedOutputId
    },

    transferHint() {
      if (!this.transferTarget) return 'Type or use keypad.'
      return this.isValidTarget(this.transferTarget) ? 'Ready to transfer.' : 'Enter an extension (2-6 digits) or a full number.'
    },
    transferEnabled() {
      return this.canTransfer && this.isValidTarget(this.transferTarget)
    },
    transferBtnClass() {
      if (!this.transferEnabled) return 'bg-gray-300 cursor-not-allowed'
      return 'bg-tvOrange hover:opacity-95 active:opacity-90'
    },

    isWlbActive() {
      return this.isWlb === true
    },
    wlbModeLabel() {
      if (this.wlbMode === 'listen') return 'Listening (silent)'
      if (this.wlbMode === 'whisper') return 'Whispering (agent hears you)'
      if (this.wlbMode === 'barge') return 'Barging (two-way)'
      return 'WLB'
    },
    wlbModePillText() {
      if (this.wlbMode === 'listen') return 'LISTEN'
      if (this.wlbMode === 'whisper') return 'WHISPER'
      if (this.wlbMode === 'barge') return 'BARGE'
      return 'WLB'
    },
    wlbModePillClass() {
      if (this.wlbMode === 'listen') return 'bg-blue-50 text-blue-700 border-blue-200'
      if (this.wlbMode === 'whisper') return 'bg-amber-50 text-amber-700 border-amber-200'
      if (this.wlbMode === 'barge') return 'bg-purple-50 text-purple-700 border-purple-200'
      return 'bg-gray-50 text-gray-700 border-gray-200'
    },
    wlbCustomerId() {
      return this.ui?.wlb?.customerId || ''
    },
    wlbTargetSip() {
      return this.ui?.wlb?.targetSipUserId || ''
    },

    filteredContacts() {
      if (!this.contactsSearch) return this.contactsList
      const q = this.contactsSearch.toLowerCase()
      return this.contactsList.filter(c => {
        return (c.name || '').toLowerCase().includes(q) ||
               (c.company || '').toLowerCase().includes(q) ||
               (c.phone || '').includes(q) ||
               (c.email || '').toLowerCase().includes(q)
      })
    },

    cdrDisplayRecents() {
      return this.cdrRecents.map(cdr => {
        const isOutbound = cdr.direction === 'outbound'
        const rawNum = isOutbound ? String(cdr.dst || '') : String(cdr.src || '')
        const number = rawNum.replace(/\D/g, '')
        const cdrLabel = isOutbound
          ? (cdr.to_label || this.formatPrettyPeer(cdr.dst))
          : (cdr.from_label || this.formatPrettyPeer(cdr.src))
        const contactMatch = this.matchContactByPhone(number)

        return {
          id: cdr._id,
          direction: cdr.direction || 'internal',
          label: contactMatch ? contactMatch.name : cdrLabel,
          number: number,
          timeAgo: this.timeAgo(cdr.calldate),
          duration: cdr.billsec || 0,
          disposition: cdr.disposition,
          answered: cdr.disposition === 'ANSWERED',
          showAddContact: !contactMatch && number.length >= 7
        }
      })
    }
  },

  watch: {
    inCall(next) {
      if (!next) {
        this.transferMode = false
        this.transferTarget = ''
        this.keypadOpen = false
      }
    },
    tab(val) {
      if (val === 'recents' && !this.cdrRecents.length && !this.cdrLoading) {
        this.loadCdrRecents()
      }
      if (val === 'contacts' && !this.contactsList.length && !this.contactsLoading) {
        this.loadContacts()
      }
    }
  },

  created() {
    this.loadContacts()
  },

  methods: {
    ...mapActions('phone', [
      'setTab',
      'openDevices',
      'closeDevices',
      'dial',
      'answer',
      'hangup',
      'toggleMute',
      'toggleHold',
      'sendDTMF',
      'refreshDevices',
      'setDevices',
      'transfer',
      'hidePhone',
      'setLocalPresenceEnabled',
      'wlbListen',
      'wlbWhisper',
      'wlbBarge'
    ]),

    closePhone() {
      this.hidePhone()
    },

    // ---------- Contacts ----------

    async loadContacts() {
      this.contactsLoading = true
      try {
        const res = await axios.get('contacts', {
          params: { per_page: 200, owner_user_id: 'all', sort_by: 'name', sort_dir: 'asc' }
        })
        this.contactsList = res?.data?.data || []
      } catch {
        this.contactsList = []
      } finally {
        this.contactsLoading = false
      }
    },

    matchContactByPhone(phone) {
      if (!phone) return null
      const digits = String(phone).replace(/\D/g, '')
      if (digits.length < 4) return null
      const last10 = digits.length >= 10 ? digits.slice(-10) : digits
      return this.contactsList.find(c => {
        if (!c.phone) return false
        const cd = String(c.phone).replace(/\D/g, '')
        if (cd.length >= 10 && last10.length >= 10) return cd.slice(-10) === last10
        return cd === digits
      }) || null
    },

    contactInitials(name) {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      return parts[0][0].toUpperCase()
    },

    dialContact(c) {
      if (!c.phone) return
      const cleaned = c.phone.replace(/\D/g, '')
      this.number = cleaned
      this.setTab('dialer')
      this.$nextTick(() => this.dialNow())
    },

    showAddContact(phone, label) {
      let cleanPhone = String(phone || '').replace(/\D/g, '')
      if (cleanPhone.length === 11 && cleanPhone.startsWith('0')) {
        cleanPhone = cleanPhone.slice(1)
      }
      const formatted = this.formatPrettyPeer(cleanPhone)
      this.addContactForm = {
        name: (label && label !== formatted && !/^\(?\d/.test(label) && !/^\+/.test(label)) ? label : '',
        phone: cleanPhone,
        company: '',
        email: ''
      }
      this.addContactMode = true
    },

    cancelAddContact() {
      this.addContactMode = false
      this.addContactForm = { name: '', phone: '', company: '', email: '' }
    },

    async saveNewContact() {
      if (!this.addContactForm.name.trim() || this.addContactSaving) return
      this.addContactSaving = true
      try {
        await axios.post('contacts', {
          name: this.addContactForm.name.trim(),
          phone: this.addContactForm.phone.trim() || null,
          company: this.addContactForm.company.trim() || null,
          email: this.addContactForm.email.trim() || null
        })
        this.addContactMode = false
        this.addContactForm = { name: '', phone: '', company: '', email: '' }
        this.loadContacts()
      } catch (e) {
        console.error('Failed to save contact:', e)
      } finally {
        this.addContactSaving = false
      }
    },

    // ---------- CDR Recents ----------

    async loadCdrRecents() {
      this.cdrLoading = true
      try {
        const res = await axios.get('cdr/user', { params: { per_page: 15, page: 1 } })
        this.cdrRecents = res?.data?.data || []
      } catch {
        this.cdrRecents = []
      } finally {
        this.cdrLoading = false
      }
    },

    timeAgo(dateStr) {
      if (!dateStr) return ''
      const now = Date.now()
      const then = new Date(dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr + 'Z').getTime()
      const diff = now - then
      const mins = Math.floor(diff / 60000)
      if (mins < 1) return 'Just now'
      if (mins < 60) return `${mins}m ago`
      const hours = Math.floor(mins / 60)
      if (hours < 24) return `${hours}h ago`
      const days = Math.floor(hours / 24)
      if (days === 1) return 'Yesterday'
      if (days < 7) return `${days}d ago`
      return new Date(then).toLocaleDateString()
    },

    // ---------- WLB ----------

    wlbSwitchDisabled(mode) {
      const ok = !!(this.ui?.wlb?.customerId && this.ui?.wlb?.targetSipUserId)
      if (!ok) return true
      if (this.wlbMode === mode && this.inCall) return true
      return false
    },
    wlbButtonClass(mode) {
      const active = this.wlbMode === mode
      if (active) return 'bg-gray-900 text-white border-gray-900'
      return 'bg-white text-gray-900 border-gray-200 hover:bg-gray-50'
    },
    async switchWlb(mode) {
      if (this.wlbSwitchDisabled(mode)) return
      const p = {
        customerId: this.ui?.wlb?.customerId,
        targetUserId: this.ui?.wlb?.targetUserId,
        targetSipUserId: this.ui?.wlb?.targetSipUserId,
        extension: this.ui?.wlb?.targetExt,
        targetName: this.ui?.wlb?.targetName
      }
      if (mode === 'listen') return this.wlbListen(p)
      if (mode === 'whisper') return this.wlbWhisper(p)
      return this.wlbBarge(p)
    },
    async hangupWlb() {
      await this.hangup()
    },

    // ---------- Dialer ----------

    append(k) {
      this.number = `${this.number}${k}`
      toneService.playDtmf(k).catch(() => {})
    },
    backspace() {
      this.number = this.number.slice(0, -1)
    },

    async dialNow() {
      const cleaned = this.cleanTarget(this.number)
      if (!this.callButtonEnabledLocal) return
      try {
        await this.dial(cleaned)
        this.keypadOpen = false
      } catch {}
    },

    dialRecent(peer) {
      this.number = peer
      this.setTab('dialer')
      this.$nextTick(() => this.dialNow())
    },

    toggleKeypad() {
      if (this.transferMode) return
      this.keypadOpen = !this.keypadOpen
    },

    toggleTransferMode() {
      if (!this.canTransfer) return
      this.transferMode = !this.transferMode
      this.transferTarget = ''
      if (this.transferMode) this.keypadOpen = false
    },

    transferAppend(k) {
      this.transferTarget = `${this.transferTarget}${k}`
      toneService.playDtmf(k).catch(() => {})
    },
    transferBackspace() {
      this.transferTarget = this.transferTarget.slice(0, -1)
    },
    async doTransfer() {
      if (!this.transferEnabled) return
      const cleaned = this.cleanTarget(this.transferTarget)
      await this.transfer(cleaned)
      this.transferMode = false
      this.transferTarget = ''
    },

    // ---------- Devices ----------

    setInput(id) {
      const inputId = id === 'null' ? null : id
      this.setDevices({ inputId, outputId: this.selectedOutputId })
    },
    setOutput(id) {
      const outputId = id === 'null' ? null : id
      this.setDevices({ inputId: this.selectedInputId, outputId })
    },

    // ---------- Helpers ----------

    isValidTarget(v) {
      const s = this.cleanTarget(v)
      if (!s) return false
      const digits = s.replace(/\D/g, '')
      if (/^\d{2,6}$/.test(digits)) return true
      if (/^\d{10,15}$/.test(digits)) return true
      return false
    },

    cleanTarget(v) {
      return String(v || '').trim().replace(/[^\d+*#]/g, '')
    },

    formatPrettyPeer(v) {
      const digits = String(v || '').replace(/\D/g, '')
      if (!digits) return ''
      if (digits.length <= 6) return digits
      if (digits.length === 11 && digits.startsWith('1')) {
        const a = digits.slice(1, 4)
        const b = digits.slice(4, 7)
        const c = digits.slice(7)
        return `+1 (${a}) ${b}-${c}`
      }
      if (digits.length === 10) {
        const a = digits.slice(0, 3)
        const b = digits.slice(3, 6)
        const c = digits.slice(6)
        return `(${a}) ${b}-${c}`
      }
      return digits
    },

    formatWhen(ts) {
      const d = new Date(ts)
      return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    },

    formatDuration(ms) {
      const totalSeconds = Math.floor((ms || 0) / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
    }
  }
}
</script>

<style scoped>
.screen-enter-active,
.screen-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}
.screen-enter-from,
.screen-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.slide-enter-active,
.slide-leave-active {
  transition: opacity 160ms ease, transform 160ms ease;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  transform: translateX(10px);
}
</style>
